import React, { useMemo } from "react";
import { useUser } from "../../context/UserContext";
import axiosInstance from "../../services/api/axios";
import { toast } from "react-toastify";
import { FormModal, Form, Field, FormSubmit } from "../../components/form";
import { PERSONA_TYPE } from "../../constants/enums";
import { createStaffLoginSchema, updateStaffLoginSchema } from "../../services/users/schemas";

/**
 * Create / edit a staff Login.
 *
 * Per the RBAC revamp the modal does NOT collect a password — the backend
 * generates one and emails it to the staff member at the email captured here.
 * Permission assignments happen separately in Role Based Access → Role Assignments.
 *
 * This is the pilot screen for the unified form/modal layer. Patterns used
 * here should be the template for every other modal migration:
 *   - <FormModal> for the shell
 *   - <Form schema={...} defaultValues={...} onSubmit={...}> for state + validation
 *   - <Field type="..."> for every input (no raw <input>/<select>)
 *   - Validation lives in a yup schema co-located with the service file, not inline.
 */
// Developers create only their own Staff. BV Users (BV Associates) are hired + assigned to projects
// internally by BuildVizer (Zumale-admin → BV Users), so they are not created from the developer side.
const USER_TYPE_OPTIONS = [{ value: PERSONA_TYPE.STAFF, label: "Staff Member" }];

const AddUserRole = ({ isOpen, onClose, selectedUser, onUserSaved }) => {
  const { userInfo } = useUser();
  const isEditMode = !!selectedUser;

  // Memoised so RHF doesn't reset on every parent re-render. Keyed by user id
  // below so a different selectedUser hydrates fresh defaults via remount.
  const defaultValues = useMemo(
    () => ({
      name: selectedUser?.name || "",
      username: selectedUser?.username || "",
      email: selectedUser?.email || "",
      mobileNoWa: selectedUser?.mobileNoWa || "",
      personaType: selectedUser?.persona || selectedUser?.personaType || PERSONA_TYPE.STAFF,
    }),
    [selectedUser],
  );

  const handleSubmit = async (data, { reset }) => {
    const loginIdentifier = userInfo?.loginIdentifierDetails?.customer?.customerId;
    const payload = {
      loginIdentifier,
      name: data.name,
      username: data.username,
      email: data.email,
      mobileNoWa: data.mobileNoWa,
      // BE ignores personaType on update — only sent on create.
      ...(isEditMode ? {} : { personaType: data.personaType }),
      active: true,
    };

    try {
      if (isEditMode) {
        await axiosInstance.put(`/login?loginId=${selectedUser.id ?? selectedUser.loginId}`, payload);
        toast.success("User updated");
      } else {
        await axiosInstance.post("/login", payload);
        toast.success(`User created. Login credentials emailed to ${data.email}.`);
      }
      reset();
      onClose();
      onUserSaved?.();
    } catch (error) {
      console.error("Error saving user:", error);
      toast.error(error?.response?.data?.detail || error?.detail || "Failed to save user");
    }
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      size="md"
      title={isEditMode ? "Edit User" : "Create User"}
    >
      <p className="text-xs text-gray-500 mb-4">
        A random password will be emailed to the user. Permissions are assigned later
        via Role Based Access → Role Assignments.
      </p>
      <Form
        // `key` forces a fresh form instance when selectedUser changes — RHF
        // doesn't auto-pick-up defaultValues changes after mount.
        key={selectedUser?.id ?? selectedUser?.loginId ?? "new"}
        schema={isEditMode ? updateStaffLoginSchema : createStaffLoginSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-3">
          <Field name="name" label="Full Name" placeholder="Enter full name" required />
          <Field name="username" label="Username" placeholder="Enter username" required />
          <Field
            name="email"
            label="Email"
            type="email"
            placeholder="Login credentials will be sent here"
            required
          />
          <Field
            name="mobileNoWa"
            label="WhatsApp Number"
            type="tel"
            placeholder="Enter WhatsApp Number"
            required
          />
          <div className="md:col-span-2">
            <Field
              name="personaType"
              label="User Type"
              type="select"
              options={USER_TYPE_OPTIONS}
              required
              isDisabled={isEditMode}
            />
            {isEditMode && (
              <p className="text-xs text-gray-500 mt-1">
                User type is fixed after creation — delete and recreate the login to switch.
              </p>
            )}
          </div>
        </div>
        <FormSubmit loadingLabel={isEditMode ? "Saving..." : "Creating..."}>
          {isEditMode ? "Save" : "Create"}
        </FormSubmit>
      </Form>
    </FormModal>
  );
};

export default AddUserRole;
